import { CallEffect, PutEffect, SelectEffect, TakeEffect, call, put, select, take } from 'redux-saga/effects';

import { appServices } from 'js/shared/helpers/app-services/AppServices';
import { getProject } from 'js/shared/helpers/app-services/AppServices/projects';
import { rendererFatalFailure, rendererLog } from 'js/shared/lib/CloudRenderer';
import { LoadScribeByIdAction, loadScribeByIdFailed, loadScribeByIdSuccess } from 'js/actionCreators/scribeActions';
import { AccountType, ExistingScribeModel, ScribeObject } from 'js/types';
import ScribeModel from 'js/models/ScribeModel';
import { ACCOUNT_TYPES } from 'js/config/consts';
import { sendErrorToSentry } from 'js/logging';
import { GET_USER_FONTS_SUCCESS, getUserFonts, loadUserFonts } from 'js/actionCreators/fontActions';
import preloadScribeFonts from 'js/shared/helpers/preloadScribeFonts';
import { trackOpenScribe } from 'js/actionCreators/trackingActions';

import { getUserAccountType } from '../selectors';

export function* loadProjectById({
  scribeId
}: LoadScribeByIdAction): Generator<
  CallEffect | PutEffect | TakeEffect | SelectEffect,
  void,
  ScribeObject | (AccountType | undefined) | ExistingScribeModel
> {
  try {
    rendererLog(`REQUEST: project data for project id: `, scribeId);
    const scribeObject = (yield call(
      getProject,
      scribeId,
      appServices.exchangeCognitoTokenForSparkolToken
    )) as ScribeObject;
    rendererLog(`SUCCESS: project data for project id: `, scribeId);

    const userAccountType = yield select(getUserAccountType);

    if (userAccountType && userAccountType !== ACCOUNT_TYPES.FREE) {
      // The view only flag from the API is determined by a user's session and when a user has upgraded the
      // session isn't renewed until they log out and log in again.
      // This ensures when the user upgrades they are able to edit their scribes in their current session
      scribeObject.viewOnly = false;
    }

    const scribe = (yield call(ScribeModel.fromObject, scribeObject)) as ExistingScribeModel;

    yield put(getUserFonts(scribeId));
    yield take(GET_USER_FONTS_SUCCESS);
    yield put(loadUserFonts(scribe));

    if (import.meta.env.VITE_DEBUG_RENDERING) {
      yield call(preloadScribeFonts, scribe);
    }

    yield put(loadScribeByIdSuccess({ scribe }));
    yield put(trackOpenScribe(scribeId));
  } catch (error) {
    rendererFatalFailure(`Project id: ${scribeId} - Failed to load data: ${(error as Error).message}`);
    yield put(loadScribeByIdFailed({ scribeId, error: error as Error }));
    console.error(error);
    sendErrorToSentry(error as Error);
  }
}
